import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import React, { useState } from "react";
import { CedarShellLayout } from "../../components/Layouts";
import { useAlerts } from "../../hooks/react-query/alerts/useAlerts";
import { useDismissedAlerts } from "../../hooks/react-query/alerts/useDismissedAlerts";
import { useSnoozedAlerts } from "../../hooks/react-query/alerts/useSnoozedAlerts";
import { useUndismissAlert } from "../../hooks/react-query/alerts/useUndismissAlert";
import { useUnsnoozeAlert } from "../../hooks/react-query/alerts/useUnsnoozeAlert";
import { AlertCard } from "./components/AlertCard";
import { AlertHistoryCard } from "./components/AlertHistoryCard";
import "../../assets/styles/pages/alertsPage.scss";

type AlertsTab = "active" | "snoozed" | "dismissed";

export const AlertsPage = () => {
  const [tab, setTab] = useState<AlertsTab>("active");
  const { alerts } = useAlerts();
  const { alerts: snoozedAlerts } = useSnoozedAlerts();
  const { alerts: dismissedAlerts } = useDismissedAlerts();
  const { mutate: unsnooze, isLoading: isUnsnoozing } = useUnsnoozeAlert();
  const { mutate: undismiss, isLoading: isUndismissing } = useUndismissAlert();

  const visibleAlerts = tab === "snoozed" ? snoozedAlerts : tab === "dismissed" ? dismissedAlerts : alerts;
  const emptyMessage =
    tab === "snoozed" ? "No snoozed alerts." : tab === "dismissed" ? "No dismissed alerts." : "No active alerts right now.";

  return (
    <CedarShellLayout>
      <div className="alerts_page" data-test-id="alerts-page">
        <div className="alerts_page_header">
          <NotificationsActiveOutlinedIcon className="page_header_icon" />
          <div>
            <h1 className="alerts_page_title">Alerts</h1>
            <p className="alerts_page_subtitle">Active alerts across your dashboards.</p>
          </div>
        </div>

        {/* "Active" isn't part of the original ask (2 buttons, for snoozed/dismissed) but is the
            only way back to the default list once either of those is open - without it, clicking
            Snoozed/Dismissed would be a one-way trip. */}
        <div className="alerts_page_tabs">
          <button
            className={`alerts_page_tab ${tab === "active" ? "alerts_page_tab--active" : ""}`}
            onClick={() => setTab("active")}
            data-test-id="alerts-tab-active"
          >
            Active
          </button>
          <button
            className={`alerts_page_tab ${tab === "snoozed" ? "alerts_page_tab--active" : ""}`}
            onClick={() => setTab("snoozed")}
            data-test-id="alerts-tab-snoozed"
          >
            Snoozed alerts{snoozedAlerts.length > 0 ? ` (${snoozedAlerts.length})` : ""}
          </button>
          <button
            className={`alerts_page_tab ${tab === "dismissed" ? "alerts_page_tab--active" : ""}`}
            onClick={() => setTab("dismissed")}
            data-test-id="alerts-tab-dismissed"
          >
            Dismissed alerts{dismissedAlerts.length > 0 ? ` (${dismissedAlerts.length})` : ""}
          </button>
        </div>

        {visibleAlerts.length === 0 ? (
          <p className="alerts_page_empty" data-test-id="alerts-page-empty">
            {emptyMessage}
          </p>
        ) : (
          <div className="alerts_page_cards">
            {tab === "active" && alerts.map((alert) => <AlertCard alert={alert} key={alert.alert_id} />)}
            {tab === "snoozed" &&
              snoozedAlerts.map((alert) => (
                <AlertHistoryCard
                  alert={alert}
                  actionLabel="Unsnooze"
                  onAction={() => unsnooze(alert.alert_id)}
                  isLoading={isUnsnoozing}
                  key={alert.alert_id}
                />
              ))}
            {tab === "dismissed" &&
              dismissedAlerts.map((alert) => (
                <AlertHistoryCard
                  alert={alert}
                  actionLabel="Undismiss"
                  onAction={() => undismiss(alert.alert_id)}
                  isLoading={isUndismissing}
                  key={alert.alert_id}
                />
              ))}
          </div>
        )}
      </div>
    </CedarShellLayout>
  );
};
